var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useRetrievePlayersByTeamId, useRetrieveTeam } from "../../ApiProvider";
import { ErrorView } from "../../nav/Error";
import { Loading } from "../../nav/Loading";
import { Bench } from "./Bench";
import { FormationPickerDrawer } from "./FormationPickerDrawer";
import { Pitch } from "./Pitch";
import { defaultFormationForTeamSize, slotsForFormation } from "./slots";
import { FORMATIONS_BY_TEAM_SIZE, TEAM_SIZES, } from "./types";
var playerFirstName = function (player) {
    var _a, _b, _c;
    var model = (_b = (_a = player.user) !== null && _a !== void 0 ? _a : player.externalUser) !== null && _b !== void 0 ? _b : null;
    return (_c = model === null || model === void 0 ? void 0 : model.firstName) !== null && _c !== void 0 ? _c : "";
};
var playerLastName = function (player) {
    var _a, _b, _c;
    var model = (_b = (_a = player.user) !== null && _a !== void 0 ? _a : player.externalUser) !== null && _b !== void 0 ? _b : null;
    return (_c = model === null || model === void 0 ? void 0 : model.lastName) !== null && _c !== void 0 ? _c : "";
};
var toFormationPlayer = function (member) {
    var _a, _b;
    return ({
        id: member.id,
        squadNumber: (_a = member.squadNumber) !== null && _a !== void 0 ? _a : null,
        firstName: playerFirstName(member),
        lastName: playerLastName(member),
        primaryPosition: (_b = member.positionOne) !== null && _b !== void 0 ? _b : null,
    });
};
var emptyAssignments = function (slotIds) {
    return slotIds.reduce(function (acc, id) {
        acc[id] = null;
        return acc;
    }, {});
};
export var FormationPage = function () {
    var _a, _b, _c, _d, _e;
    var id = useParams().id;
    var teamId = id ? Number(id) : undefined;
    var _f = __read(useState(11), 2), teamSize = _f[0], setTeamSize = _f[1];
    var _g = __read(useState(defaultFormationForTeamSize(11)), 2), formation = _g[0], setFormation = _g[1];
    var slots = useMemo(function () { return slotsForFormation(formation); }, [formation]);
    var slotIds = useMemo(function () { return slots.map(function (s) { return s.id; }); }, [slots]);
    var _h = __read(useState(function () {
        return emptyAssignments(slotIds);
    }), 2), assignments = _h[0], setAssignments = _h[1];
    var _j = __read(useState(null), 2), selected = _j[0], setSelected = _j[1];
    var _k = __read(useState(null), 2), pickerSlotId = _k[0], setPickerSlotId = _k[1];
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    var _l = useRetrieveTeam({ id: teamId }), team = _l.data, isTeamLoading = _l.isLoading, teamError = _l.error;
    var _m = useRetrievePlayersByTeamId({ teamId: teamId }), _o = _m.data, members = _o === void 0 ? [] : _o, isMembersLoading = _m.isLoading, membersError = _m.error;
    // Only active squad members are selectable — inactive players shouldn't
    // appear on the bench or be assignable to the pitch.
    var allPlayers = useMemo(function () { return members.filter(function (m) { return m.isActive; }).map(toFormationPlayer); }, [members]);
    var assignedIds = useMemo(function () {
        return new Set(Object.values(assignments)
            .filter(function (p) { return p !== null; })
            .map(function (p) { return p.id; }));
    }, [assignments]);
    var benchPlayers = useMemo(function () { return allPlayers.filter(function (p) { return !assignedIds.has(p.id); }); }, [allPlayers, assignedIds]);
    var handleSizeChange = function (_, value) {
        if (value === null)
            return;
        var nextFormation = defaultFormationForTeamSize(value);
        setTeamSize(value);
        setFormation(nextFormation);
        setAssignments(emptyAssignments(slotsForFormation(nextFormation).map(function (s) { return s.id; })));
        setSelected(null);
        setPickerSlotId(null);
    };
    var handleFormationChange = function (event) {
        var next = event.target.value;
        setFormation(next);
        setAssignments(emptyAssignments(slotsForFormation(next).map(function (s) { return s.id; })));
        setSelected(null);
        setPickerSlotId(null);
    };
    var handleSlotDrop = function (item, targetSlotId) {
        setAssignments(function (prev) {
            var _a;
            var next = __assign({}, prev);
            var occupant = (_a = prev[targetSlotId]) !== null && _a !== void 0 ? _a : null;
            if (item.source === "bench") {
                next[targetSlotId] = item.player;
            }
            else {
                next[targetSlotId] = item.player;
                next[item.slotId] = occupant;
            }
            return next;
        });
    };
    var handleBenchDrop = function (item) {
        if (item.source !== "pitch")
            return;
        setAssignments(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[item.slotId] = null, _a)));
        });
    };
    // Dropping a pitch player onto a specific bench player → they swap places:
    // the bench player takes over the pitch slot, the pitch player goes to the
    // bench. Implemented by just replacing the slot's occupant; the original
    // pitch player drops out of assignments and reappears on the bench.
    var handleBenchPlayerSwap = function (pitchSlotId, benchPlayer) {
        setAssignments(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[pitchSlotId] = benchPlayer, _a)));
        });
    };
    var isSameTarget = function (sel, target) {
        if (!sel)
            return false;
        if (target.kind === "pitchSlot")
            return sel.source === "pitch" && sel.slotId === target.slotId;
        if (target.kind === "benchPlayer")
            return sel.source === "bench" && sel.playerId === target.player.id;
        return false;
    };
    var selectionToDragItem = function (sel) {
        if (!sel)
            return null;
        if (sel.source === "pitch") {
            var player_1 = assignments[sel.slotId];
            if (!player_1)
                return null;
            return { source: "pitch", player: player_1, slotId: sel.slotId };
        }
        var player = allPlayers.find(function (p) { return p.id === sel.playerId; });
        if (!player)
            return null;
        return { source: "bench", player: player };
    };
    // On mobile, tapping a pitch slot opens the picker drawer instead of
    // running the desktop click-select flow.
    var handlePitchSlotClick = function (slotId, player) {
        if (isMobile) {
            setSelected(null);
            setPickerSlotId(slotId);
            return;
        }
        handleClick({ kind: "pitchSlot", slotId: slotId, player: player });
    };
    var handleDrawerSelectPlayer = function (player) {
        if (pickerSlotId === null)
            return;
        handleSlotDrop({ source: "bench", player: player }, pickerSlotId);
        setPickerSlotId(null);
    };
    var handleDrawerRemove = function () {
        if (pickerSlotId === null)
            return;
        setAssignments(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[pickerSlotId] = null, _a)));
        });
        setPickerSlotId(null);
    };
    var handleClick = function (target) {
        if (!selected) {
            if (target.kind === "pitchSlot" && target.player) {
                setSelected({
                    source: "pitch",
                    slotId: target.slotId,
                    playerId: target.player.id,
                });
            }
            else if (target.kind === "benchPlayer") {
                setSelected({ source: "bench", playerId: target.player.id });
            }
            return;
        }
        if (isSameTarget(selected, target)) {
            setSelected(null);
            return;
        }
        var dragItem = selectionToDragItem(selected);
        if (!dragItem) {
            setSelected(null);
            return;
        }
        if (target.kind === "pitchSlot") {
            handleSlotDrop(dragItem, target.slotId);
            setSelected(null);
        }
        else if (target.kind === "benchPlayer") {
            if (dragItem.source === "pitch") {
                handleBenchPlayerSwap(dragItem.slotId, target.player);
                setSelected(null);
            }
            else {
                setSelected({ source: "bench", playerId: target.player.id });
            }
        }
        else {
            if (dragItem.source === "pitch")
                handleBenchDrop(dragItem);
            setSelected(null);
        }
    };
    if (isTeamLoading || isMembersLoading) {
        return _jsx(Loading, {});
    }
    if (teamError || membersError || !team) {
        return (_jsx(ErrorView, { error: (_b = (_a = teamError) !== null && _a !== void 0 ? _a : membersError) !== null && _b !== void 0 ? _b : new Error("Team not found") }));
    }
    var primaryColour = (_c = team.primaryColour) !== null && _c !== void 0 ? _c : "#1d4ed8";
    var formationOptions = FORMATIONS_BY_TEAM_SIZE[teamSize];
    // Single source of truth for the pitch row's geometry on sm+. Width is
    // derived from the same height so the pitch and bench wrappers share an
    // exact, definite pixel height (no aspect-ratio rounding mismatch).
    var PITCH_HEIGHT_SM = "calc(100vh - 72px)";
    var PITCH_WIDTH_SM = "calc((100vh - 72px) * 2 / 3)";
    return (_jsxs(Container, { maxWidth: "lg", sx: { py: 2 }, children: [_jsxs(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(180px, 220px) auto minmax(200px, 260px)",
                    },
                    gridTemplateAreas: {
                        xs: "\"controls\" \"pitch\"",
                        sm: "\"controls pitch bench\"",
                    },
                    gridTemplateRows: { sm: PITCH_HEIGHT_SM },
                    gap: 2,
                    alignItems: "stretch",
                    justifyContent: { sm: "center" },
                }, children: [_jsxs(Box, { sx: {
                            gridArea: "controls",
                            display: "flex",
                            flexDirection: { xs: "row", sm: "column" },
                            alignItems: { xs: "center", sm: "stretch" },
                            flexWrap: "wrap",
                            gap: 1.5,
                        }, children: [_jsxs(Typography, { variant: "h6", component: "h1", sx: {
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    width: { xs: "100%", sm: "auto" },
                                    mb: { xs: 0, sm: 0.5 },
                                }, children: [team.name, " \u2014 Formation"] }), _jsxs(Box, { sx: { width: { xs: "auto", sm: "100%" } }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", sx: {
                                            display: { xs: "none", sm: "block" },
                                            mb: 0.5,
                                            lineHeight: 1,
                                        }, children: "Team size" }), _jsx(ToggleButtonGroup, { value: teamSize, exclusive: true, onChange: handleSizeChange, size: "small", "aria-label": "Team size", children: TEAM_SIZES.map(function (size) { return (_jsx(ToggleButton, { value: size, "aria-label": "".concat(size, "-a-side"), sx: { px: 1.5, py: 0.25, fontSize: "0.8rem" }, children: size }, size)); }) })] }), _jsxs(FormControl, { size: "small", sx: { minWidth: 140, width: { xs: "auto", sm: "100%" } }, children: [_jsx(InputLabel, { id: "formation-label", children: "Formation" }), _jsx(Select, { labelId: "formation-label", id: "formation", value: formation, label: "Formation", onChange: handleFormationChange, children: formationOptions.map(function (f) { return (_jsx(MenuItem, { value: f, children: f }, f)); }) })] })] }), _jsx(Box, { sx: {
                            gridArea: "pitch",
                            width: { xs: "100%", sm: PITCH_WIDTH_SM },
                            height: { xs: "auto", sm: PITCH_HEIGHT_SM },
                            aspectRatio: { xs: "2 / 3", sm: "unset" },
                            justifySelf: { sm: "center" },
                        }, children: _jsx(Pitch, { slots: slots, assignments: assignments, primaryColour: primaryColour, onDrop: handleSlotDrop, selectedSlotId: (selected === null || selected === void 0 ? void 0 : selected.source) === "pitch" ? selected.slotId : null, onClickSlot: handlePitchSlotClick }) }), !isMobile && (_jsx(Box, { sx: {
                            gridArea: "bench",
                            minWidth: 0,
                            height: PITCH_HEIGHT_SM,
                            display: "flex",
                        }, children: _jsx(Bench, { players: benchPlayers, primaryColour: primaryColour, onDrop: handleBenchDrop, onSwapBenchPlayer: handleBenchPlayerSwap, selectedPlayerId: (selected === null || selected === void 0 ? void 0 : selected.source) === "bench" ? selected.playerId : null, onClickPlayer: function (player) {
                                return handleClick({ kind: "benchPlayer", player: player });
                            }, onClickArea: function () { return handleClick({ kind: "benchArea" }); } }) }))] }), _jsx(FormationPickerDrawer, { open: pickerSlotId !== null, slot: (_d = slots.find(function (s) { return s.id === pickerSlotId; })) !== null && _d !== void 0 ? _d : null, currentPlayer: pickerSlotId ? (_e = assignments[pickerSlotId]) !== null && _e !== void 0 ? _e : null : null, benchPlayers: benchPlayers, primaryColour: primaryColour, onClose: function () { return setPickerSlotId(null); }, onSelectBenchPlayer: handleDrawerSelectPlayer, onRemoveCurrent: handleDrawerRemove })] }));
};
